import type { ReactNode } from "react"
import {
	IconArrowLeftOutline18 as ArrowLeft,
	IconChevronLeftOutline18 as ChevronLeft,
	IconMoonOutline18 as MoonIcon,
	IconMagnifierOutline18 as Search,
	IconSunOutline18 as SunIcon,
} from "nucleo-ui-outline-18"
import { z } from "zod"

import { cn } from "@productlane/lib/src/cn"
import {
	Badge,
	Button,
	Form,
	FormInput,
	Input,
	useZodForm,
} from "@productlane/ui"
import {
	AgentOrb,
	agentOrbColorsFromAccent,
} from "@productlane/ui/src/components/agent-orb/agent-orb"

import type { NavItem } from "@/screens/app/sidebar"
import { ColorSwatchPicker } from "@/components/color-swatch-picker"
import { isDevEnvironment } from "@/env"
import { useZeroWorkspace } from "@/lib/zero"
import { NotFound } from "@/screens/not-found"
import { PortalPreview } from "../../settings/portal-preview"
import { SettingsHeader } from "../../settings/settings-header"
import {
	NAV_ROW_ICON_GAP_CLASS,
	NAV_ROW_ICON_SIZE_CLASS,
	NAV_ROW_PADDING_LEFT_CLASS,
	NAV_ROW_VERTICAL_PADDING_CLASS,
} from "../../settings/settings-nav-geometry"
import {
	DarkModeField,
	EmbedImageField,
} from "../../settings/settings-pages/theme-fields"
import {
	SETTINGS_PAGE_PADDING_X_CLASS,
	SettingsCardInsetContext,
	SettingsContainer,
} from "../../settings/settings-switches/settings-container"
import {
	ACCOUNT_NAV_ITEMS,
	FEATURES_NAV_ITEMS,
	SETUP_NAV_ITEMS,
	WORKSPACE_NAV_ITEMS,
} from "../../settings/sidebar"
import { DesignIssueHeader } from "./design-issue-header"

/* ------------------------------------------------------------------ */
/* Settings shell replica                                              */
/* ------------------------------------------------------------------ */

function StaticNavRow({ item, active }: { item: NavItem; active: boolean }) {
	const Icon = item.icon
	return (
		<div
			className={cn(
				"flex w-full items-center rounded-md pr-2",
				NAV_ROW_VERTICAL_PADDING_CLASS,
				NAV_ROW_PADDING_LEFT_CLASS,
				active ? "bg-gray-150 dark:bg-gray-800" : "bg-transparent",
			)}
		>
			<Icon
				strokeWidth={1}
				className={cn(
					"flex flex-none items-center justify-center text-gray-800 dark:text-gray-400",
					NAV_ROW_ICON_GAP_CLASS,
					NAV_ROW_ICON_SIZE_CLASS,
				)}
			/>
			<div
				className={cn(
					"flex-1 select-none truncate text-left text-sm font-medium",
					active ? "text-primary" : "text-secondary dark:text-gray-400",
				)}
			>
				{item.title}
			</div>
		</div>
	)
}

function NavGroup({ title, items }: { title: string; items: NavItem[] }) {
	return (
		<div className="mt-4 flex w-full flex-col gap-0.5 px-2.5 first:mt-0 first:pt-3">
			<div className="pb-1 pl-2 text-xs font-normal text-tertiary">{title}</div>
			{items.map((item) => (
				<StaticNavRow
					key={item.href}
					item={item}
					active={item.href === "/settings/portal"}
				/>
			))}
		</div>
	)
}

function SettingsRail() {
	return (
		<div className="w-[15.1rem] flex-none border-r border-gray-200 bg-gray-100 pb-20 dark:border-gray-700/80 dark:bg-gray-950">
			<div className="w-full px-2.5 pb-4 pt-2.5">
				<div
					className={cn(
						"mb-2.5 flex w-full items-center rounded-md pr-2",
						NAV_ROW_PADDING_LEFT_CLASS,
						NAV_ROW_VERTICAL_PADDING_CLASS,
					)}
				>
					<ArrowLeft
						strokeWidth={1}
						className={cn(
							"flex-none text-tertiary",
							NAV_ROW_ICON_GAP_CLASS,
							NAV_ROW_ICON_SIZE_CLASS,
						)}
					/>
					<span className="flex-1 select-none truncate text-xs text-tertiary">
						Back to app
					</span>
				</div>
				<div className="flex h-7 items-center gap-2 rounded-md bg-white pl-2 text-sm text-tertiary ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-700/80">
					<Search strokeWidth={1} className="size-4 flex-none" />
					Search settings
				</div>
			</div>
			<NavGroup title="Administration" items={WORKSPACE_NAV_ITEMS} />
			<NavGroup title="Personal" items={ACCOUNT_NAV_ITEMS} />
			<NavGroup title="Setup" items={SETUP_NAV_ITEMS} />
			<NavGroup title="Features" items={FEATURES_NAV_ITEMS} />
		</div>
	)
}

function SettingsShell({ children }: { children: ReactNode }) {
	return (
		<div className="space-y-3">
			<div className="font-mono text-xs text-tertiary">
				Settings / Portal / Theme (/settings/portal/theme)
			</div>
			<div className="rounded-2xl bg-gray-100 p-2 dark:bg-gray-950">
				<div
					className="flex overflow-hidden rounded-2xl bg-gray-25 shadow-md ring-1 ring-black/10 dark:bg-gray-900 dark:shadow-black dark:ring-white/15"
					translate="no"
				>
					<SettingsRail />
					<div className="flex min-w-0 flex-1 flex-col">
						<div className="block px-8 pt-4">
							<Button variant="ghost" Icon={ChevronLeft}>
								Portal
							</Button>
						</div>
						<main
							className={cn(
								"mx-auto flex w-full max-w-[1212px] shrink-0 grow cursor-default flex-col px-5 py-16",
								SETTINGS_PAGE_PADDING_X_CLASS,
							)}
						>
							<SettingsCardInsetContext.Provider value={true}>
								{children}
							</SettingsCardInsetContext.Provider>
						</main>
					</div>
				</div>
			</div>
		</div>
	)
}

function New() {
	return (
		<Badge variant="green" size="sm">
			New
		</Badge>
	)
}

/* ------------------------------------------------------------------ */
/* Theme page replica, the orb placed per version                      */
/* ------------------------------------------------------------------ */

export type OrbPlacement = "field" | "row" | "preview"

const ThemeSchema = z.object({
	accentColor: z.string(),
	darkModeAccentColor: z.string(),
	buttonTextColor: z.string(),
	darkModeButtonTextColor: z.string(),
})
type ThemeValues = z.infer<typeof ThemeSchema>
type ColorField = keyof ThemeValues

const DARK_BACKGROUND = "#050506"

function ColorRow({
	label,
	field,
	value,
	onChange,
	orb,
}: {
	label: string
	field: ColorField
	value: string
	onChange: (hex: string) => void
	orb?: ReactNode
}) {
	return (
		<div>
			<div className="text-xs font-medium text-secondary">{label}</div>
			<div className="mt-1 flex items-start gap-2">
				<ColorSwatchPicker value={value} onChange={onChange} />
				<FormInput name={field} className="flex-1" />
				{orb}
			</div>
		</div>
	)
}

function BackgroundRow({ fallback }: { fallback: string }) {
	return (
		<div>
			<div className="text-xs font-medium text-secondary">Background color</div>
			<div className="mt-1 flex items-start gap-2">
				<ColorSwatchPicker value={fallback} onChange={() => undefined} />
				<Input
					name="background"
					className="flex-1"
					value=""
					placeholder={`Default (${fallback})`}
					onChange={() => undefined}
				/>
			</div>
		</div>
	)
}

/** A: the orb sits at the end of each accent field, on that mode's own background. */
function FieldOrb({ hex, dark }: { hex: string; dark?: boolean }) {
	return (
		<div className="flex items-center gap-1.5 self-center">
			<span
				className={cn(
					"flex size-9 flex-none items-center justify-center rounded-lg ring-1",
					dark
						? "ring-white/10"
						: "bg-white ring-gray-200 dark:ring-gray-700/80",
				)}
				style={dark ? { background: DARK_BACKGROUND } : undefined}
			>
				<AgentOrb size={24} colors={agentOrbColorsFromAccent(hex)} />
			</span>
		</div>
	)
}

/** B: its own row above the colors, both modes side by side. */
function AgentRow({ light, dark }: { light: string; dark: string }) {
	const tile = (hex: string, mode: "Light" | "Dark") => (
		<div
			className={cn(
				"flex flex-1 items-center gap-3 rounded-xl px-4 py-3 ring-1",
				mode === "Dark" ? "text-white ring-white/10" : "bg-white ring-gray-200",
			)}
			style={mode === "Dark" ? { background: DARK_BACKGROUND } : undefined}
		>
			<AgentOrb
				size={40}
				state="talking"
				colors={agentOrbColorsFromAccent(hex)}
			/>
			<div className="min-w-0">
				<div className="text-sm font-medium">{mode} mode</div>
				<div
					className={cn(
						"truncate text-xs",
						mode === "Dark" ? "text-white/60" : "text-secondary",
					)}
				>
					From {hex.toUpperCase()}
				</div>
			</div>
		</div>
	)
	return (
		<div className="flex flex-col gap-3">
			<div>
				<div className="flex items-center gap-2">
					<p className="text-sm font-medium">Agent</p>
					<New />
				</div>
				<p className="text-xs text-secondary">
					The agent&apos;s orb takes its colors from your accent.
				</p>
			</div>
			<div className="flex gap-3">
				{tile(light, "Light")}
				{tile(dark, "Dark")}
			</div>
		</div>
	)
}

/** C: a small agent preview under the portal preview, as the widget shows it. */
function AgentPreviewCard({ light, dark }: { light: string; dark: string }) {
	const bar = (hex: string, isDark: boolean) => (
		<div
			className="flex items-center gap-2.5 rounded-full py-2 pl-2.5 pr-4 shadow-[0_1px_2px_rgba(0,0,0,0.06),0_8px_16px_-6px_rgba(0,0,0,0.2)] ring-1"
			style={{
				background: isDark ? "#1c1c1f" : "#ffffff",
				color: isDark ? "rgba(255,255,255,0.6)" : "#6b6b72",
				borderColor: "transparent",
			}}
		>
			<AgentOrb size={26} colors={agentOrbColorsFromAccent(hex)} />
			<span className="truncate text-sm">How can I help?</span>
		</div>
	)
	return (
		<div className="mt-4 overflow-hidden rounded-xl ring-1 ring-gray-200 dark:ring-gray-700/80">
			<div className="flex items-center justify-between border-b border-gray-200 px-4 py-2.5 dark:border-gray-700/80">
				<span className="text-xs font-medium text-secondary">Agent</span>
				<New />
			</div>
			<div className="grid grid-cols-2">
				<div className="flex items-center justify-center bg-gray-50 px-4 py-6">
					{bar(light, false)}
				</div>
				<div
					className="flex items-center justify-center px-4 py-6"
					style={{ background: DARK_BACKGROUND }}
				>
					{bar(dark, true)}
				</div>
			</div>
		</div>
	)
}

export function ThemePageWithOrb({
	placement,
	initial,
}: {
	placement: OrbPlacement
	initial: ThemeValues
}) {
	const workspace = useZeroWorkspace()
	const form = useZodForm({ schema: ThemeSchema, defaultValues: initial })
	const values = form.watch()
	const set = (field: ColorField) => (hex: string) =>
		form.setValue(field, hex, { shouldDirty: true })

	return (
		<>
			<SettingsHeader
				title="Theme"
				subtitle="Customize the theme of your widget and portal."
			/>
			<SettingsCardInsetContext.Provider value={false}>
				<div className="flex flex-col gap-8 lg:flex-row lg:justify-between">
					<div className="min-w-0 flex-1 lg:max-w-2xl">
						<SettingsContainer>
							<Form
								className="space-y-8"
								form={form}
								onSubmit={() => undefined}
							>
								<div className="flex flex-wrap items-center justify-between gap-4">
									<div>
										<p className="text-sm font-medium">Logo</p>
										<p className="text-xs text-secondary">
											Shown in the portal header, the widget and your emails.
											256x256 px recommended.
										</p>
									</div>
									<span className="flex size-12 items-center justify-center overflow-hidden rounded-xl bg-gray-100 ring-1 ring-gray-200 dark:bg-gray-800 dark:ring-gray-700/80">
										{workspace?.logoUrl && (
											<img
												src={workspace.logoUrl}
												alt=""
												className="size-full object-cover"
											/>
										)}
									</span>
								</div>
								<hr className="border-gray-200 dark:border-gray-700/80" />
								<EmbedImageField
									imageUrl={workspace?.portalEmbedImageUrl ?? null}
									onUploaded={() => Promise.resolve()}
									onCleared={() => Promise.resolve()}
								/>
								<hr className="border-gray-200 dark:border-gray-700/80" />
								<DarkModeField
									value={workspace?.darkMode ?? null}
									onChange={() => undefined}
								/>
								<hr className="border-gray-200 dark:border-gray-700/80" />
								{placement === "row" && (
									<>
										<AgentRow
											light={values.accentColor}
											dark={values.darkModeAccentColor}
										/>
										<hr className="border-gray-200 dark:border-gray-700/80" />
									</>
								)}
								<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
									<div>
										<div className="mb-2 flex items-center gap-2">
											<div className="text-sm font-medium">Light mode</div>
											<SunIcon className="h-4 w-4 text-secondary" />
										</div>
										<div className="grid grid-cols-1 gap-4">
											<ColorRow
												label="Accent color"
												field="accentColor"
												value={values.accentColor}
												onChange={set("accentColor")}
												orb={
													placement === "field" && (
														<FieldOrb hex={values.accentColor} />
													)
												}
											/>
											<ColorRow
												label="Button text color"
												field="buttonTextColor"
												value={values.buttonTextColor}
												onChange={set("buttonTextColor")}
											/>
											<BackgroundRow fallback="#FFFFFF" />
										</div>
									</div>
									<div>
										<div className="mb-2 flex items-center gap-2">
											<div className="text-sm font-medium">Dark mode</div>
											<MoonIcon className="h-4 w-4 text-secondary" />
										</div>
										<div className="grid grid-cols-1 gap-4">
											<ColorRow
												label="Accent color"
												field="darkModeAccentColor"
												value={values.darkModeAccentColor}
												onChange={set("darkModeAccentColor")}
												orb={
													placement === "field" && (
														<FieldOrb hex={values.darkModeAccentColor} dark />
													)
												}
											/>
											<ColorRow
												label="Button text color"
												field="darkModeButtonTextColor"
												value={values.darkModeButtonTextColor}
												onChange={set("darkModeButtonTextColor")}
											/>
											<BackgroundRow fallback={DARK_BACKGROUND} />
										</div>
									</div>
								</div>
								{placement === "field" && (
									<div className="-mt-4 flex items-center gap-2 text-xs text-secondary">
										<New />
										The orb beside each accent is your agent in that color.
									</div>
								)}
							</Form>
						</SettingsContainer>
					</div>
					<div className="w-full lg:w-[420px] lg:flex-shrink-0">
						<PortalPreview
							workspace={workspace}
							settingsHash={JSON.stringify(values)}
							hasAnyPortalEnabled
						/>
						{placement === "preview" && (
							<AgentPreviewCard
								light={values.accentColor}
								dark={values.darkModeAccentColor}
							/>
						)}
					</div>
				</div>
			</SettingsCardInsetContext.Provider>
		</>
	)
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

const VERSIONS: { label: string; note: string; placement: OrbPlacement }[] = [
	{
		label: "Version A",
		note: "The orb sits at the end of each accent field, on that mode's background. Smallest change.",
		placement: "field",
	},
	{
		label: "Version B",
		note: "An Agent row above the colors shows the orb in light and dark at a size you can judge.",
		placement: "row",
	},
	{
		label: "Version C",
		note: "Under the portal preview, the spotlight bar as visitors see it, light and dark.",
		placement: "preview",
	},
]

export function ThemeOrbPage() {
	const workspace = useZeroWorkspace()
	if (!isDevEnvironment) return <NotFound />
	const initial: ThemeValues = {
		accentColor: workspace?.accentColor ?? "#4CB782",
		darkModeAccentColor: workspace?.darkModeAccentColor ?? "#6EC499",
		buttonTextColor: workspace?.buttonTextColor ?? "#FFFFFF",
		darkModeButtonTextColor: workspace?.darkModeButtonTextColor ?? "#FFFFFF",
	}
	return (
		<div className="min-h-screen space-y-12 bg-gray-50 px-10 py-10 pb-24">
			<div className="max-w-[1200px]">
				<DesignIssueHeader
					identifier="Design"
					title="Show the agent orb next to the portal accent color"
					url="http://100.90.47.10:5173/settings/portal/theme"
					summary="The agent's orb takes its colors from the portal accent, so the Theme page should show what a color does to it. Three placements, each on the full Theme page with your workspace's real colors. Type a color into any accent field to see the orb follow it. Green New marks what each version adds; everything else matches production. The copy is a placeholder and goes through wording before it ships."
				/>
			</div>
			<div className="flex flex-nowrap items-start gap-10 overflow-x-auto pb-4">
				{VERSIONS.map((version) => (
					<div key={version.label} className="w-[1480px] flex-none space-y-6">
						<div className="space-y-1">
							<div className="text-base font-semibold text-primary">
								{version.label}
							</div>
							<div className="text-sm text-secondary">{version.note}</div>
						</div>
						<SettingsShell>
							{workspace && (
								<ThemePageWithOrb
									placement={version.placement}
									initial={initial}
								/>
							)}
						</SettingsShell>
					</div>
				))}
			</div>
		</div>
	)
}
